import _ from 'lodash';

import { FeeSchedule } from '../../admin/types/FeeSchedule';
import { UserLog } from '../../admin/types/UserLog';
import { SubscriptionType } from '../../florence/types/SubscriptionType';
import { Code, SavedCode } from '../types/Codes';
import {
  Organization,
  OrganizationToSave,
  OrganizationWithOwner,
  QuickOrganization,
} from '../types/Organization';
import { PaginatedResponse } from '../types/PaginatedResponse';
import { PaginationParams } from '../types/PaginationParams';
import { SheetStatus } from '../types/SaveSheet';
import { SearchResult, SingleSearchResult } from '../types/SearchResult';
import {
  CreateUserPayload,
  CreateUserResponse,
  QuickUser,
  User,
  UserRoles,
} from '../types/User';

import { ENDPOINTS } from './endpoints';

import apiInstance from '.';

const adminApi = apiInstance.injectEndpoints({
  overrideExisting: false,
  endpoints: (builder) => ({
    updateScans: builder.mutation<
      { message: string },
      { id: string; scans: number }
    >({
      query: ({ id, scans }) => ({
        url: `${ENDPOINTS.adminGetUsers}${id}/update-scans`,
        method: 'POST',
        body: {
          scans,
        },
      }),
      invalidatesTags: (_results, _error, { id }) => [
        'Users',
        { type: 'Users', id },
      ],
    }),
    overwriteSubscription: builder.mutation<
      { message: string },
      { id: string; subscription: SubscriptionType }
    >({
      query: ({ id, subscription }) => ({
        url: `${ENDPOINTS.adminGetUsers}${id}/overwrite-subscription`,
        method: 'POST',
        body: {
          subscription,
        },
      }),
      invalidatesTags: (_results, _error, { id }) => [
        'Users',
        { type: 'Users', id },
      ],
    }),
    updateAthenaUsername: builder.mutation<
      { message: string },
      { id: string; usernames: string }
    >({
      query: ({ id, usernames }) => ({
        url: `${ENDPOINTS.adminGetUsers}${id}/update-athena-username`,
        method: 'POST',
        body: {
          usernames,
        },
      }),
      invalidatesTags: (_results, _error, { id }) => [
        'Users',
        { type: 'Users', id },
      ],
    }),
    getUserLogs: builder.query<UserLog[], string>({
      query: (id) => ({
        url: `${ENDPOINTS.adminGetUserLogs}${id}`,
        method: 'GET',
      }),
    }),
    getUsers: builder.query<
      PaginatedResponse<User>,
      PaginationParams & {
        search?: string;
        sortField?: string;
        direction?: string;
        subscriptions?: string[];
        onlyActiveUsers?: boolean;
      }
    >({
      query: (params) => ({
        url: `${ENDPOINTS.adminGetUsers}`,
        method: 'GET',
        params,
      }),
      transformResponse: (response: PaginatedResponse<User>) => {
        const { docs: users } = response;
        response.docs = users.map((user) => {
          user.fullName = (
            (user.firstName ?? '') +
            ' ' +
            (user.lastName ?? '')
          ).trim();
          user.subscription = _.startCase(
            user.lastKnownSubscription?.toString() ?? 'no subscription'
          );
          return user;
        });
        return response;
      },
      providesTags: (result) =>
        result
          ? [...result.docs.map(({ id }) => ({ type: 'Users' as const, id }))]
          : ['Users'],
    }),
    getUsersQuick: builder.query<QuickUser[], void>({
      query: () => ({
        url: `${ENDPOINTS.adminGetUsersQuick}`,
        method: 'GET',
      }),
    }),
    createUser: builder.mutation<CreateUserResponse, CreateUserPayload>({
      query: (body) => ({
        url: `${ENDPOINTS.adminCreateUser}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Users'],
    }),
    getResults: builder.query<
      PaginatedResponse<SearchResult>,
      PaginationParams & {
        search?: string;
        sortField?: string;
        direction?: string;
        statuses?: string[];
      }
    >({
      query: (params) => ({
        url: `${ENDPOINTS.adminGetSearchResults}`,
        method: 'GET',
        params,
      }),
      transformResponse: (response: PaginatedResponse<SearchResult>) =>
        response,
      providesTags: ['SearchResults'],
    }),
    getSingleResult: builder.query<
      SingleSearchResult,
      {
        id: string;
      }
    >({
      query: ({ id }) => ({
        url: `${ENDPOINTS.adminGetSearchResults}${id}`,
        method: 'GET',
      }),
    }),
    getAdminSearchResultsByUser: builder.query<
      SingleSearchResult[],
      { id: string; clinicNotes: boolean }
    >({
      query: ({ id, clinicNotes }) => ({
        url: `${ENDPOINTS.adminGetSearchResults}user/${id}`,
        method: 'GET',
        params: { clinicNotes },
      }),
    }),
    getAdminFavoriteCodes: builder.query<SavedCode[], string>({
      query: (id) => ({
        url: `${ENDPOINTS.adminGetFavoriteCodes}${id}`,
        method: 'GET',
      }),
      transformResponse: (response: SavedCode[]) => response?.reverse(),
    }),
    getAdminFlaggedCodes: builder.query<Code[], string>({
      query: (id) => ({
        url: `${ENDPOINTS.adminGetFlaggedCodes}${id}`,
        method: 'GET',
      }),
    }),
    getUser: builder.query<User, string>({
      query: (id) => ({
        url: `${ENDPOINTS.adminGetUsers}${id}`,
        method: 'GET',
      }),
      providesTags: (result) => [{ type: 'Users', id: result?.id }],
    }),
    enableUser: builder.mutation<User, string>({
      query: (id) => ({
        url: `${ENDPOINTS.adminGetUsers}${id}/enable-user`,
        method: 'POST',
      }),
      invalidatesTags: (_restuls, _error, id) => [
        'Users',
        { type: 'Users', id },
      ],
    }),
    disableUser: builder.mutation<User, string>({
      query: (id) => ({
        url: `${ENDPOINTS.adminGetUsers}${id}/disable-user`,
        method: 'POST',
      }),
      invalidatesTags: (_restuls, _error, id) => [
        'Users',
        { type: 'Users', id },
      ],
    }),
    changeNoteStatus: builder.mutation<
      { message: string },
      { sheetId: number; status: SheetStatus }
    >({
      query: ({ sheetId, status }) => ({
        url: `${ENDPOINTS.adminChangeNoteStatus}`,
        method: 'POST',
        body: {
          sheetId,
          status,
        },
      }),
      invalidatesTags: ['SearchResults'],
    }),
    getOrganizations: builder.query<
      PaginatedResponse<Organization>,
      PaginationParams & {
        search?: string;
        sortField?: string;
        direction?: string;
      }
    >({
      query: (params) => ({
        url: `${ENDPOINTS.organizations}`,
        method: 'GET',
        params,
      }),
      transformResponse: (response: PaginatedResponse<Organization>) =>
        response,
      providesTags: ['Organizations'],
    }),
    getOrganizationsQuick: builder.query<QuickOrganization[], void>({
      query: () => ({
        url: `${ENDPOINTS.organizations}quick`,
        method: 'GET',
      }),
      providesTags: ['Organizations'],
    }),
    getOrganizationMembers: builder.query<User[], { id: string }>({
      query: ({ id }) => ({
        url: `${ENDPOINTS.organizations}${id}/users`,
        method: 'GET',
      }),
      providesTags: ['Users', 'Organizations'],
    }),
    getOrganization: builder.mutation<
      { organization: OrganizationWithOwner },
      { id: string }
    >({
      query: ({ id }) => ({
        url: `${ENDPOINTS.organizations}${id}`,
        method: 'GET',
      }),
    }),
    activateOrgSubscription: builder.mutation<
      { message: string },
      { id: string }
    >({
      query: ({ id }) => ({
        url: `${ENDPOINTS.organizations}${id}/activate-subscription`,
        method: 'POST',
      }),
    }),
    saveOrganization: builder.mutation<{ message: string }, OrganizationToSave>(
      {
        query: (organization) => ({
          url: `${ENDPOINTS.organizations}`,
          method: 'POST',
          body: organization,
        }),
        invalidatesTags: ['Organizations'],
      }
    ),
    updateOrganization: builder.mutation<
      { message: string },
      { id: string; data: OrganizationToSave }
    >({
      query: ({ id, data }) => ({
        url: `${ENDPOINTS.organizations}${id}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['Organizations'],
    }),
    deleteOrganization: builder.mutation<{ message: string }, { id: string }>({
      query: ({ id }) => ({
        url: `${ENDPOINTS.organizations}${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Organizations'],
    }),
    changeUserOrganization: builder.mutation<
      { message: string },
      { userId: number; organizationName: string }
    >({
      query: ({ userId, organizationName }) => ({
        url: `${ENDPOINTS.organizations}change-user-organization`,
        method: 'POST',
        body: {
          organizationName,
          userId: userId,
        },
      }),
      invalidatesTags: (_results, _error, { userId }) => [
        'Users',
        { type: 'Users', userId },
      ],
    }),
    removeUserFromOrganization: builder.mutation<
      { message: string },
      { userId: number }
    >({
      query: ({ userId }) => ({
        url: `${ENDPOINTS.organizations}remove-user-from-organization`,
        method: 'POST',
        body: {
          userId: userId,
        },
      }),
      invalidatesTags: (_results, _error, { userId }) => [
        'Users',
        { type: 'Users', userId },
      ],
    }),
    changeUserRole: builder.mutation<
      { message: string },
      { userId: number; newRole: UserRoles }
    >({
      query: ({ userId, newRole }) => ({
        url: `${ENDPOINTS.organizations}change-user-role`,
        method: 'POST',
        body: {
          role: newRole,
          userId: userId,
        },
      }),
      invalidatesTags: (_results, _error, { userId }) => [
        'Users',
        { type: 'Users', userId },
      ],
    }),
    createOrUpdateMisc: builder.mutation<
      { message: string },
      { key: string; value: string }
    >({
      query: ({ key, value }) => ({
        url: ENDPOINTS.misc,
        method: 'POST',
        body: {
          key,
          value,
        },
      }),
    }),
    getMiscByKey: builder.query<{ key: string; value: string }, string>({
      query: (key) => ({
        url: `${ENDPOINTS.misc}${key}`,
        method: 'GET',
      }),
    }),
    sendAIRequest: builder.mutation<string, string>({
      query: (message) => ({
        url: `${ENDPOINTS.misc}ai`,
        method: 'POST',
        body: {
          message,
        },
      }),
    }),
    createOrUpdateFeeSchedule: builder.mutation<
      { message: string },
      Omit<FeeSchedule, 'createdAt' | 'updatedAt'>
    >({
      query: (data) => ({
        url: ENDPOINTS.feeSchedules,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['FeeSchedules'],
    }),
    deleteFeeSchedule: builder.mutation<{ message: string }, { id: string }>({
      query: (data) => ({
        url: ENDPOINTS.feeSchedules,
        method: 'DELETE',
        body: data,
      }),
      invalidatesTags: ['FeeSchedules'],
    }),
    getFeeSchedules: builder.query<FeeSchedule[], void>({
      query: () => ({
        url: ENDPOINTS.feeSchedules,
        method: 'GET',
      }),
      providesTags: ['FeeSchedules'],
    }),
  }),
});

export const {
  useGetUsersQuery,
  useLazyGetUsersQuery,
  useGetUsersQuickQuery,
  useCreateUserMutation,
  useGetResultsQuery,
  useGetSingleResultQuery,
  useEnableUserMutation,
  useDisableUserMutation,
  useGetUserLogsQuery,
  useGetUserQuery,
  useGetAdminSearchResultsByUserQuery,
  useGetAdminFavoriteCodesQuery,
  useGetAdminFlaggedCodesQuery,
  useUpdateScansMutation,
  useUpdateAthenaUsernameMutation,
  useOverwriteSubscriptionMutation,
  useChangeNoteStatusMutation,
  useGetOrganizationsQuery,
  useGetOrganizationsQuickQuery,
  useGetOrganizationMutation,
  useActivateOrgSubscriptionMutation,
  useGetOrganizationMembersQuery,
  useUpdateOrganizationMutation,
  useSaveOrganizationMutation,
  useDeleteOrganizationMutation,
  useChangeUserOrganizationMutation,
  useChangeUserRoleMutation,
  useRemoveUserFromOrganizationMutation,
  useCreateOrUpdateMiscMutation,
  useGetMiscByKeyQuery,
  useSendAIRequestMutation,
  useCreateOrUpdateFeeScheduleMutation,
  useGetFeeSchedulesQuery,
  useDeleteFeeScheduleMutation,
} = adminApi;
export default adminApi;
